// DeviceID -> human label + which control screen to use. Mirrors the Flutter
// screenRedirector() mapping (docs/03-devices.md).

import { channelsFor, fanChannelsFor, masterChannelsFor } from './statusFormat';
import { panelColumns } from './buttonLayout';
import { isCurtainDevice, curtainNoun } from './curtain';
import { deviceCatalogEntry as catalogEntry } from './deviceCatalog';
import { plateFor, plateGridSpan, PLATE_CARD_WIDTH } from './glassPlates';
import { HUB_DEVICE_ID } from './hubDevice';

export const ONE_TOUCH_IDS = [5, 6, 21, 22, 28, 31, 33, 40, 42, 55, 59, 60, 80];

// Combined switch + fan panels: DeviceID 92 = "4 + 1" (4 switches + 1 fan),
// DeviceID 93 = "7 + 1". Flutter routes both to switchFanScreen with a different
// switchCount — SWITCH_FAN_COUNT is that count.
export const SWITCH_FAN_COUNT = { 92: 4, 93: 7 };

/*
 * BLE LED lamp group — "Ble Wwh Dimmable Controller". A DeviceID 25 look-alike
 * (white + warm white) whose members are cheap LampSmart Pro / FanLamp Pro
 * ceiling lamps driven by BLE ADVERTISING from the phone, not by MQTT. There
 * is no ESP behind it and no status ever comes back. See docs/14-ble-lamps.md.
 *
 * ⚠️ Must match BLE_WWH_DEVICE_ID in the backend's api/new/bleLamps.js and the
 * DeviceType row inserted by ble_lamps_migration.sql.
 */
export const BLE_WWH_DEVICE_ID = 100;
export const isBleLampDevice = (deviceId) => Number(deviceId) === BLE_WWH_DEVICE_ID;

/*
 * DeviceID 101 — the 4 Scene Controller. Four touch buttons, NO outputs: a press
 * runs scene N of the room the panel is in, on the SERVER (POST /api/RunScene).
 * The app draws the four scene buttons and the room's step lists; nothing on
 * it is switched. See core/scenes/sceneController and docs/07-scenes.md.
 */
export const SCENE_CONTROLLER_DEVICE_ID = 101;

/*
 * Door locks — DeviceID 3 "Door Lock" (the main door) and 102 "Room Door Lock",
 * added 2026-09-28 as a copy of 3: same SmartLock type, one output, unlock
 * behind the user's PIN. Both open DoorScreen.
 *
 * ⚠️ THE UNLOCK WORD DIFFERS: 3 takes OPENDOOR, 102 takes LOCKUNLOCK. A wrong
 * word is not an error at either end — the door just stays shut. Every sender
 * (both door screens, the desktop card, a Wall Mode lock slot) goes through
 * unlockCommand; never write either literal in a screen.
 *
 * ⚠️ Only 3 is in the backend's hard-coded room-list exclusions (3, 19, 21),
 * so 102 arrives through the ordinary room query — no third call needed.
 */
export const DOOR_LOCK_IDS = [3, 102];
export const ROOM_DOOR_LOCK_ID = 102;
export const unlockCommand = (deviceId) =>
  (Number(deviceId) === ROOM_DOOR_LOCK_ID ? 'LOCKUNLOCK' : 'OPENDOOR');
/** The unlock button's words — 102 gets its own explicit button (sends LOCKUNLOCK). */
export const unlockLabel = (deviceId) =>
  (Number(deviceId) === ROOM_DOOR_LOCK_ID ? 'Open Room Door Lock' : 'Unlock');

export function deviceTypeKey(deviceId) {
  const id = Number(deviceId);
  if (id === 49) return 'fourTouch';
  if (id === 56) return 'twoTouch';
  if (id === 53) return 'fan';
  /*
   * Curtains and the sliding gate — one key, because they are one control.
   *
   * ⚠️ 13 AND 37 USED TO FALL THROUGH TO THE GENERIC OUTPUT UI, which gave a
   * curtain two independent relay toggles labelled "Curtain Open" / "Curtain
   * Close". That is the wrong shape twice over: the relays are momentary, so a
   * toggle has no OFF to correspond to, and the pair has no interlock — the
   * firmware admits as much (`OtaForTuya.ino:4758`), so both directions could be
   * energised from the screen. 7 Curtain Controllers were deployed like that.
   *
   * The gate is included on the firmware's own say-so: DeviceType 37 accepts the
   * curtain words *"because the app presents the gate with the same UI as a
   * curtain"* (`OtaForTuya.ino:6039`). See core/devices/curtain.js — including
   * why 78 and 79 stay out.
   */
  if (isCurtainDevice(id)) return 'curtain';
  if (id === 24) return 'rgb';
  // 90 "Wwh Dimmable Spot Light" is the same product shape as 25
  // (ColorController, Outputs 1, Dimming 1 — docs/10) and fell to 'unsupported'.
  if (id === 25 || id === 90) return 'wwh';
  if (id === BLE_WWH_DEVICE_ID) return 'bleWwh';
  // The Wi-Fi + BLE hub: infrastructure with NO buttons, whatever output rows
  // registration gave it (core/devices/hubDevice, ui/shared/HubInfo).
  if (id === HUB_DEVICE_ID) return 'hub';
  if (id === SCENE_CONTROLLER_DEVICE_ID) return 'sceneController';
  if (id === 23) return 'wwrgb';
  if (DOOR_LOCK_IDS.includes(id)) return 'door';
  if (id === 82) return 'singlePhase';
  if (id === 16) return 'threePhase';
  if (id === 92 || id === 93) return 'switchFan';
  /*
   * IR blasters — keypads, not switches. Zero outputs, so before this they fell
   * to the generic card and told the owner of a working blaster
   * "No controllable outputs found".
   *
   * ⚠️ 19 ONLY IS PROVEN ON HARDWARE. Flutter's gate was `DeviceID in
   * (19,57,58,62)` (repository.dart:1257-1259) and copying that set wholesale is
   * the trap:
   *   • 58 "Ac On Off" has **Outputs 1** — it is a RELAY with a real switch UI,
   *     and giving it this key would take that away. Excluded.
   *   • 57 and 62 share the shape (0 outputs, On_off 1) but run a different
   *     firmware image — 19 is OTABinFileId 7 (OTA_AIRCONDITIONER_IR.ino.bin)
   *     while 57/62 point at 1 — and there is not one 57/58/62 row in the live
   *     DevicesInstalled to test against. Routed here because a remote is a far
   *     better guess than "no outputs", but the vocabulary is unconfirmed.
   */
  if (id === 19 || id === 57 || id === 62) return 'irBlaster';
  if (ONE_TOUCH_IDS.includes(id)) return 'oneTouch';

  /*
   * ── Everything else, from the backend's own catalogue ────────────────────
   *
   * Reached only after every hand-written case above has declined, so this can
   * never take a product away from a screen built for it — the explicit list
   * wins by construction, and stays right when the catalogue has not loaded.
   *
   * What it adds is every switch bank nobody has written a branch for. On the
   * live table that is not a handful: 0 (6+1), 1 (4+1), 10 (Tv Stand), 11
   * (5+1), 17 (5+2), 44, 68 (a bare BLDC fan), 87 (7+2), 91, and the eight new
   * glass panels 92–99. Ten of those were drawing generic output toggles.
   *
   * ⚠️ SENSORS CANNOT REACH HERE ANYWAY, and that is not luck: all three
   * routers — mobile DeviceScreen, desktop DeviceControls, desktop
   * DeviceControlCard — test `isSensorDevice(device)` BEFORE asking for a key.
   * The `Switch` test below is the second line of that defence, not the first.
   */
  const e = catalogEntry(id);
  if (e && e.type === 'Switch' && e.consistent) {
    // A fan and no switches is a fan, not a one-tile panel with a dial bolted
    // to it — DeviceID 68 "Bldc Fan Switch" is On_off 0, Dimming 1.
    if (e.onOff === 0 && e.dimming > 0) return 'fan';
    if (e.dimming > 0 && e.onOff > 0) return 'switchFan';
    // Reuse the purpose-built keys where the width matches one, so a catalogue
    // 4-gang is drawn by exactly the same screen as DeviceID 49.
    if (e.onOff === 1) return 'oneTouch';
    if (e.onOff === 2) return 'twoTouch';
    if (e.onOff === 4) return 'fourTouch';
    if (e.onOff > 0) return 'touch';
  }
  return 'unsupported';
}

/**
 * How many plain on/off relays a device exposes — NOT counting fan channels.
 *
 * Drives the tile grid and how much of the status frame is read as booleans.
 */
export function relayCount(deviceId) {
  const id = Number(deviceId);
  const e = catalogEntry(id);
  switch (deviceTypeKey(id)) {
    case 'fourTouch': return 4;
    case 'twoTouch': return 2;
    case 'oneTouch': return 1;
    // ⚠️ The catalogue FIRST here, unlike the routing above: SWITCH_FAN_COUNT
    // knows only 92 and 93, and its `|| 4` default would draw a 5+1 as a 4+1
    // and quietly lose a load. The table stays as the offline fallback.
    case 'switchFan': return (e && e.onOff) || SWITCH_FAN_COUNT[id] || 4;
    case 'touch': return (e && e.onOff) || 0;
    default: return 0;
  }
}

/** Fan channels on a switch panel — the trailing values, carrying speeds. */
export function fanCount(deviceId) {
  const k = deviceTypeKey(deviceId);
  if (k !== 'switchFan' && k !== 'touch') return 0;
  return fanChannelsFor(deviceId) || 0;
}

/** Does this panel's glass carry a master touch position? */
export function hasMasterButton(deviceId) {
  const k = deviceTypeKey(deviceId);
  if (k !== 'switchFan' && k !== 'touch' && k !== 'fourTouch'
    && k !== 'twoTouch' && k !== 'oneTouch') return false;
  return masterChannelsFor(deviceId) > 0;
}

// Grid column span per device. The 5-channel WWRGB light is wider so all its
// sliders fit; everything else uses the dense default.
/**
 * How wide a device's card needs to be.
 *
 * ⚠️ A PANEL IS AS WIDE AS ITS CHANNEL COUNT MAKES IT. This used to key on the
 * TYPE alone, so a 7+1 — eight channels, four panel columns — was given the
 * same quarter-row as a one-gang switch. Four labelled buttons cannot fit
 * ~55 px each, so they overflowed their grid tracks and drew on top of one
 * another: the overlap in the Bag room.
 *
 * The panel is two rows tall (buttonLayout.PANEL_ROWS), so the columns — not
 * the channels — are what set the width: 4 channels is 2 columns, 6 is 3, 8 is
 * 4. Growing with the columns keeps every button at a usable size instead of
 * compressing past the point of being readable.
 */
export function deviceGridSpan(deviceId) {
  // A glass plate is as wide as its module count (core/devices/glassPlates).
  const plate = plateFor(deviceId);
  if (plate) return plateGridSpan(plate.modules);
  const k = deviceTypeKey(deviceId);
  // ⚠️ One column-step narrower than before at md/lg (2026-10-08, "space is
  // wasted"): the desktop theme is dense and the relay tiles are shorter, so a
  // 4-gang reads fine at a quarter of the row — four cards across, not three.
  if (k === 'wwrgb') return { xs: 12, sm: 6, md: 6, lg: 3 };
  if (k === 'singlePhase' || k === 'threePhase') return { xs: 12, sm: 6, md: 6, lg: 3 };
  // A remote card carries a handful of shortcut keys plus a link to the pads —
  // the full 33-key TV pad belongs on the device screen, not in a room grid.
  if (k === 'irBlaster') return { xs: 12, sm: 6, md: 4, lg: 3 };
  // Four scene buttons in a 2×2 — the width of a 4-gang card.
  if (k === 'sceneController') return { xs: 12, sm: 6, md: 4, lg: 3 };
  const cols = panelColumns(channelsFor(deviceId) || 1);
  if (cols >= 4) return { xs: 12, sm: 6, md: 4, lg: 3 };
  if (cols === 3) return { xs: 12, sm: 6, md: 3, lg: 3 };
  return { xs: 6, sm: 4, md: 2, lg: 2 };
}

/**
 * Fixed card width in px, for the horizontal strips that cannot use a grid.
 *
 * Same rule as deviceGridSpan and the same reason — a strip that hands every
 * card 230 px squeezes an eight-channel panel into four 50 px columns.
 */
export function deviceCardWidth(deviceId) {
  const plate = plateFor(deviceId);
  if (plate) return PLATE_CARD_WIDTH[plate.modules] || 400;
  const k = deviceTypeKey(deviceId);
  if (k === 'singlePhase' || k === 'threePhase' || k === 'wwrgb') return 380;
  const cols = panelColumns(channelsFor(deviceId) || 1);
  // ~100 px a column plus the card's own padding. Was 78 for the old flat
  // pills; the phone-style RelayTile (2026-10-03) needs room for its badges and a
  // two-line name, or a 4/8-gang card crushes the tiles together.
  return Math.max(230, 100 * cols + 40);
}

/*
 * What a device IS, for a bench. `DeviceName` is whatever the installer typed —
 * a 4 Scene Controller on the bench today is named "4 touch switch", and the
 * console was showing only that, so the operator could not tell which type the
 * row was on without opening the admin page. The type name comes from the
 * `DeviceType` table row joined into GetDeviceDetailbyUniqueNumber
 * (`DeviceTypeName`), falling back to the app's own label, and the DeviceID is
 * spelled out because the number is what every table, doc and firmware build
 * is keyed on.
 */
export function deviceTypeCaption(device) {
  if (!device || device.DeviceID == null) return '';
  const name = device.DeviceTypeName || deviceLabel(device.DeviceID) || 'Unknown type';
  return `${name} · DeviceID ${device.DeviceID}`;
}

export function deviceLabel(deviceId) {
  switch (deviceTypeKey(deviceId)) {
    // "Curtain" or "Sliding Gate" — same control, and a driveway gate labelled
    // "Curtain" is how an operator ends up doubting they are on the right row.
    case 'curtain': return curtainNoun(deviceId) === 'Gate' ? 'Sliding Gate' : 'Curtain';
    case 'fourTouch': return '4-Gang Switch';
    case 'twoTouch': return '2-Gang Switch';
    case 'oneTouch': return 'Switch';
    case 'fan': return 'Fan';
    case 'rgb': return 'RGB Light';
    case 'wwh': return 'White Light';
    case 'bleWwh': return 'BLE Lamp Group';
    case 'sceneController': return '4 Scene Controller';
    case 'wwrgb': return 'WWRGB Light';
    case 'door': return 'Door Lock';
    case 'irBlaster': return 'IR Remote';
    case 'singlePhase': return 'Energy Meter';
    case 'threePhase': return '3-Phase Meter';
    // "7 + 1", "5 + 2" — both numbers from the catalogue, because the fan count
    // is not always one and a 5+2 labelled "5 + 1" is a load nobody looks for.
    case 'switchFan': return `${relayCount(deviceId)} + ${fanChannelsFor(deviceId) || 1} Switch/Fan`;
    case 'touch': return `${relayCount(deviceId)}-Gang Switch`;
    default: return 'Device';
  }
}
